/**
 * `/why-ryerson/ryerson-commitment/`: the Ryerson Commitment landing page. On any other
 * `NEXT_PUBLIC_COMPANY` the page isn't built. SEO fields and the site-search entry come from
 * `src/content/ryerson/why-ryerson/ryerson-commitment.ts`; each section keeps its copy beside its
 * component.
 *
 * Below the hero (`components/WhyRyerson/RyersonCommitment/RyersonCommitment.tsx`) comes one
 * teaser per topic, each from its folder under `components/WhyRyerson/RyersonCommitment/`:
 * Product Quality, Certification & Compliance, Sustainability, Ryerson Gives Back, Environment,
 * Health & Safety (the `EnvironmentHealthAndSafety/` component, not the `EnvironmentHealthSafety/`
 * sections of the sub-page) and ESG. The teasers are rendered directly, not through `AllSections`.
 *
 * The only `AllSections` entry is Contact Us, marked `hide`: from `lg` up `AllSections` renders it,
 * and below `lg` it is left out of the accordion and the page renders the one-button
 * `ContactUsRefactor` itself.
 */
import React from "react";
import { AllSections, Sections } from "@components/Shared/AllSections/AllSections";
import RyersonCommitment from "@components/WhyRyerson/RyersonCommitment/RyersonCommitment";
import ProductQuality from "@components/WhyRyerson/RyersonCommitment/ProductQuality/ProductQuality";
import Sustainability from "@components/WhyRyerson/RyersonCommitment/Sustainability/Sustainability";
import EnvironmentHealthAndSafety from "@components/WhyRyerson/RyersonCommitment/EnvironmentHealthAndSafety/EnvironmentHealthAndSafety";
import CertificateAndCompliance from "@components/WhyRyerson/RyersonCommitment/CertificationAndCompliance/CertificateAndCompliance";
import RyersonGivesBack from "@components/WhyRyerson/RyersonCommitment/RyersonGivesBack/RyersonGivesBack";
import ESG from "@components/WhyRyerson/RyersonCommitment/ESG/ESG";
import ContactUsRefactor from "@components/Shared/ContactUsRefactor/ContactUs";
import { DefaultContactUsContent } from "@components/Shared/ContactUsRefactor/Content";
import { Media } from "@ryerson/frontend.next";
import { useApplication } from "@ryerson/frontend.application";
import MetaHead from "@components/Shared/model/MetaHead";
import { MetaObject } from "@content/ryerson/why-ryerson/ryerson-commitment";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";

/** Ryerson build only. Fetches the header and footer data from Contentful at build time. */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	return await allContentfulHeaderCalls();
};

/** Renders the landing page in the current language; see the file header for the layout. */
const Page: React.FC<HeaderPageProps> = (props) => {
	const {
		localization: { language },
	} = useApplication();

	const sections: Sections[] = [
		{
			title: DefaultContactUsContent[language].title,
			cmp: <ContactUsRefactor oneButtonVariant={false} />,
			hide: true,
		},
	];

	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<RyersonCommitment />
			<ProductQuality />
			<CertificateAndCompliance />
			<Sustainability />
			<RyersonGivesBack />
			<EnvironmentHealthAndSafety />
			<ESG />
			<AllSections sections={sections} />
			<Media lessThan="lg">
				<ContactUsRefactor
					oneButtonVariant={true}
					withImage={false}
					background="secondary"
				/>
			</Media>
		</HeaderLayout>
	);
};

export default Page;
